/**
 * Card Component - Constants & Configuration
 * ============================================
 *
 * Este archivo contiene todas las constantes y configuraciones
 * del componente Card. Centralizar estos valores facilita:
 * - Mantenimiento y actualizaciones
 * - Reutilización en tests
 * - Documentación automática
 * - Consistencia visual
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Variantes de color disponibles
 */
export type CardVariant = 'default' | 'primary' | 'success' | 'prime' | 'warning' | 'danger';

/**
 * Tamaños disponibles
 */
export type CardSize = 'sm' | 'md' | 'lg';

/**
 * Layout del card
 * - stack:      layout clásico vertical (imagen/título/contenido/acciones) — DEFAULT, comportamiento histórico
 * - sport-row:  tarjeta de deporte horizontal (avatar + nombre + chip Prime) — variante Figma
 * - sport-tile: tarjeta de deporte vertical compacta (avatar + nombre + caption) — variante Figma
 */
export type CardLayout = 'stack' | 'sport-row' | 'sport-tile';

/**
 * Estilos de variante
 */
export interface VariantStyles {
  container: string;
  header: string;
  badge: string;
}

/**
 * Estilos de tamaño
 */
export interface SizeStyles {
  padding: string;
  title: string;
  subtitle: string;
  body: string;
  image: string;
}

/**
 * Clases base del componente
 */
export interface BaseClasses {
  container: string;
  elevated: string;
  disabled: string;
}

/**
 * Valores por defecto
 */
export interface DefaultValues {
  variant: CardVariant;
  size: CardSize;
  layout: CardLayout;
  elevated: boolean;
  disabled: boolean;
}

/**
 * Nombres de bindings por categoría
 */
export interface BindingCategories {
  content: string[];
  appearance: string[];
  actions: string[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Variantes de color disponibles para el Card
 *
 * Cada variante define clases Tailwind para:
 * - container: Estilos del contenedor principal
 * - header: Estilos del título
 * - badge: Estilos del badge/etiqueta
 */
export const VARIANTS: Record<CardVariant, VariantStyles> = {
  default: {
    container: 'ec-bg-color-surface',
    header: 'ec-text-color-text',
    badge: 'ec-bg-color-bg ec-text-color-text-muted'
  },
  primary: {
    container: 'ec-bg-color-surface ec-border-2 ec-border-color-primary',
    header: 'ec-text-color-primary',
    badge: 'ec-bg-color-primary-light ec-text-color-primary-dark'
  },
  success: {
    container: 'ec-bg-white ec-border-2 ec-border-green-500',
    header: 'ec-text-green-700',
    badge: 'ec-bg-green-100 ec-text-green-700'
  },
  prime: {
    container: 'ec-bg-gradient-to-r ec-from-prime ec-to-prime-dark ec-text-white',
    header: 'ec-text-white',
    badge: 'ec-bg-white/20 ec-text-white'
  },
  warning: {
    container: 'ec-bg-white ec-border-2 ec-border-warning',
    header: 'ec-text-amber-700',
    badge: 'ec-bg-amber-100 ec-text-amber-700'
  },
  danger: {
    container: 'ec-bg-white ec-border-2 ec-border-danger',
    header: 'ec-text-danger',
    badge: 'ec-bg-danger-light ec-text-red-700'
  }
};

/**
 * Tamaños disponibles para el Card
 */
export const SIZES: Record<CardSize, SizeStyles> = {
  sm: {
    padding: 'ec-p-3',
    title: 'ec-text-sm',
    subtitle: 'ec-text-xs',
    body: 'ec-text-sm',
    image: 'ec-h-32'
  },
  md: {
    padding: 'ec-p-4',
    title: 'ec-text-base',
    subtitle: 'ec-text-sm',
    body: 'ec-text-sm',
    image: 'ec-h-40'
  },
  lg: {
    padding: 'ec-p-6',
    title: 'ec-text-lg',
    subtitle: 'ec-text-base',
    body: 'ec-text-base',
    image: 'ec-h-48'
  }
};

/**
 * Clases CSS base del componente
 */
export const BASE_CLASSES: BaseClasses = {
  container: 'ec-rounded-2xl ec-transition-card ec-cursor-pointer ec-overflow-hidden',
  elevated: 'ec-shadow-card hover:ec-shadow-card-hover hover:-ec-translate-y-0.5',
  disabled: 'ec-opacity-50 ec-cursor-not-allowed'
};

/**
 * Valores por defecto del componente
 */
export const DEFAULTS: DefaultValues = {
  variant: 'default',
  size: 'md',
  layout: 'stack',
  elevated: true,
  disabled: false
};

/**
 * Clases del chip "Precio exclusivo Prime" (variantes sport)
 * `prime` es un token de canales RGB → admite el modificador de opacidad `/10`.
 */
export const SPORT_PRIME_CHIP =
  'ec-inline-flex ec-items-center ec-bg-prime/10 ec-text-prime ec-font-semibold ec-text-xs ec-rounded-full ec-px-2 ec-py-1';

/**
 * Clases del avatar circular (variantes sport)
 * `color-primary-light` es hex (sin opacidad) — sirve de placeholder cuando no hay imagen.
 */
export const SPORT_AVATAR =
  'ec-rounded-full ec-overflow-hidden ec-bg-color-primary-light ec-flex ec-items-center ec-justify-center';

/**
 * Nombres de los bindings del componente
 * Útil para documentación y validación
 */
export const BINDING_NAMES: BindingCategories = {
  content: ['title', 'subtitle', 'description', 'badge', 'imageUrl', 'imageAlt'],
  appearance: ['variant', 'size', 'elevated', 'disabled'],
  actions: ['onClick', 'primaryAction', 'primaryActionText', 'secondaryAction', 'secondaryActionText']
};
